/**
 * useGeoInBackground - Background Geographic Detection
 * 
 * Определяет геопозицию в фоне без блокировки UI
 * Возвращает текущее состояние для отображения в компактном виде
 * 
 * Flow:
 * 1. Call /api/geo-detect (Vercel Edge) - получить геоданные
 * 2. Call /functions/v1/geo-preflight - проверить ограничения
 * 3. Update state без блокировки UI
 * 4. FAIL-OPEN: если ошибка, показываем форму с warning
 * 
 * Owner: @i (Auth Flow Optimization)
 * Phase: Background geo detection
 * Created: 2025-11-05
 */

import { devLog } from '@/lib/logger';
import { useEffect, useState } from 'react';

interface GeoRestrictions {
  has_restrictions: boolean;
  restriction_level: 'none' | 'info' | 'warning' | 'soft_block' | 'hard_block';
  restriction_tier?: string;
  affected_services: string[];
  message: string;
  transparency_note: string;
  can_proceed: boolean;
  alternative_options?: string[];
  official_source_url?: string;
}

interface GeoInBackgroundState {
  // Состояние загрузки
  isDetecting: boolean;

  // Результаты
  country: string | null;
  city: string | null;
  restrictions: GeoRestrictions | null;
  vpnDetected: boolean;

  // Ошибки (fail-open)
  error: string | null;

  // Confidence
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Hook для фоновой загрузки геопозиции
 * Не блокирует UI, работает параллельно с формой
 */
export function useGeoInBackground(): GeoInBackgroundState {
  const [state, setState] = useState<GeoInBackgroundState>({
    isDetecting: true,
    country: null,
    city: null,
    restrictions: null,
    vpnDetected: false,
    error: null,
    confidence: 'low'
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Step 1: Detect geo (Vercel Edge)
        devLog('[useGeoInBackground] Starting geo detection...');
        const geoResponse = await fetch('/api/geo-detect', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (cancelled) return;

        if (!geoResponse.ok) {
          throw new Error('Geo detection failed');
        }

        const geoData = await geoResponse.json();
        devLog('[useGeoInBackground] Geo data received:', geoData);

        // Step 2: Check restrictions (Supabase Edge)
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        // eslint-disable-next-line internal/no-direct-edge-fetch -- Special case: unauthenticated geo preflight before login
        const restrictionsResponse = await fetch(
          `${supabaseUrl}/functions/v1/geo-preflight`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': anonKey,
              'Authorization': `Bearer ${anonKey}`
            },
            body: JSON.stringify({
              detected_country: geoData.country,
              detected_city: geoData.city,
              detected_latitude: geoData.latitude ? parseFloat(geoData.latitude) : null,
              detected_longitude: geoData.longitude ? parseFloat(geoData.longitude) : null,
              detection_source: geoData.source,
              detection_confidence: geoData.confidence,
              vpn_detected: geoData.vpn_suspected,
              session_id: crypto.randomUUID()
            })
          }
        );

        if (cancelled) return;

        if (!restrictionsResponse.ok) {
          throw new Error('Restrictions check failed');
        }

        const restrictionsData = await restrictionsResponse.json();
        devLog('[useGeoInBackground] Restrictions data received:', restrictionsData);

        // Update state with results
        setState({
          isDetecting: false,
          country: restrictionsData.detected_country,
          city: restrictionsData.detected_city,
          restrictions: restrictionsData.restrictions,
          vpnDetected: restrictionsData.vpn_detected,
          error: null,
          confidence: restrictionsData.detection_confidence
        });

        // Store in sessionStorage for persistence across page reloads
        try {
          sessionStorage.setItem('geo_background', JSON.stringify({
            country: restrictionsData.detected_country,
            city: restrictionsData.detected_city,
            vpn: restrictionsData.vpn_detected,
            restrictions: restrictionsData.restrictions,
            timestamp: new Date().toISOString()
          }));
        } catch (storageError) {
          devLog('[useGeoInBackground] Failed to store in sessionStorage:', storageError);
        }

      } catch (error) {
        if (cancelled) return;

        // FAIL-OPEN: geo detection failure is non-critical, use warn not error
        devLog('[useGeoInBackground] Non-critical detection failure:', error);

        // FAIL-OPEN: показываем форму с warning
        setState({
          isDetecting: false,
          country: null,
          city: null,
          restrictions: null,
          vpnDetected: false,
          error: error instanceof Error ? error.message : 'Detection failed',
          confidence: 'low'
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

